/**
 * EppKioskSign — pantalla pública accedida por el móvil del colaborador
 * al escanear el QR generado por almacén/SGSSO al momento de entregar EPP.
 * NO requiere login: valida el token efímero y permite firmar la constancia.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { ShieldCheck, CheckCircle, Warning, DeviceMobile } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import SignaturePad from "@/components/SignaturePad";

export default function EppKioskSign() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const base = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError("Token faltante en el QR."); setLoading(false); return; }
    axios.get(`${base}/api/epp/kiosk/sessions/${token}`)
      .then((r) => { setSession(r.data); setLoading(false); })
      .catch((e) => {
        setError(e?.response?.data?.detail || "QR inválido o expirado.");
        setLoading(false);
      });
  }, [base, token]);

  const submit = async () => {
    if (!signature) { toast.warning("Dibuja tu firma antes de confirmar"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${base}/api/epp/kiosk/sessions/sign`,
        { token, signature_data_url: signature });
      setDone(true);
      toast.success(`Firma registrada · Folio ${r.data.folio || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al firmar");
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
      <p data-testid="epp-kiosk-loading">Cargando…</p>
    </div>;
  }
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-red-950 to-slate-900 text-white flex items-center justify-center p-4">
        <div className="bg-slate-900 border border-red-700 rounded-2xl p-8 text-center max-w-md" data-testid="epp-kiosk-error">
          <Warning size={48} weight="fill" className="text-red-500 mx-auto mb-3" />
          <p className="font-heading font-black text-xl">QR inválido</p>
          <p className="text-sm text-slate-300 mt-2">{error}</p>
          <p className="text-xs text-slate-400 mt-3">Pide a almacén / SGSSO que genere un QR nuevo.</p>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 text-white flex items-center justify-center p-4">
        <div className="bg-slate-900 border border-emerald-700 rounded-2xl p-8 text-center max-w-md" data-testid="epp-kiosk-done">
          <CheckCircle size={64} weight="fill" className="text-emerald-400 mx-auto mb-3" />
          <p className="font-heading font-black text-2xl">✓ Constancia firmada</p>
          <p className="text-sm text-slate-300 mt-2">
            Tu firma quedó registrada en la constancia de EPP.
            Ya puedes cerrar esta ventana.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white p-4" data-testid="epp-kiosk-page">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <DeviceMobile size={28} weight="fill" className="text-blue-400" />
          <div>
            <p className="font-heading font-black text-lg leading-none">Firma de Constancia EPP</p>
            <p className="text-[11px] text-slate-400 mt-1">SGSSO — Equipo de Protección Personal</p>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 mb-4">
          <p className="text-[11px] text-slate-400 uppercase font-bold tracking-wider">Colaborador</p>
          <p className="text-lg font-bold mt-0.5" data-testid="epp-kiosk-emp">{session.employee_name}</p>
          <p className="text-xs text-slate-400">{session.employee_position} · {session.employee_crew}</p>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <div><span className="text-slate-500">Folio: </span><span className="font-mono">{session.folio}</span></div>
            <div><span className="text-slate-500">Fecha: </span>{session.date}</div>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 mb-4">
          <p className="text-[11px] text-slate-400 uppercase font-bold tracking-wider mb-2">EPP a recibir</p>
          <div className="space-y-2" data-testid="epp-kiosk-items">
            {(session.items || []).map((it, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <ShieldCheck size={16} weight="fill" className="text-blue-400 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-slate-100">{it.item_description}</p>
                  <p className="text-[10px] text-slate-500">
                    {it.qty} × <span className="uppercase">{it.reason}</span>
                    {it.size ? ` · Talla ${it.size}` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-900 border border-blue-700 rounded-xl p-4 mb-4">
          <p className="text-[10px] text-slate-300 leading-relaxed">
            <b>Declaro:</b> haber recibido en buen estado el(los) EPP arriba descrito(s),
            comprometiéndome a portarlo(s) durante toda la jornada, mantenerlo(s) en
            condiciones de uso y reportar inmediatamente al supervisor cualquier daño o
            pérdida. El incumplimiento constituye falta al reglamento interno de SGSSO.
          </p>
        </div>

        <div className="bg-white rounded-xl p-3 mb-4">
          <p className="text-[10px] uppercase font-bold text-slate-600 mb-1">Firma aquí</p>
          <SignaturePad value={signature} onChange={setSignature} width={340} height={140}
                        testid="epp-kiosk-sig" />
          <div className="flex justify-between items-center mt-2">
            <button onClick={() => setSignature("")}
                    className="text-[11px] text-slate-500 underline"
                    data-testid="epp-kiosk-clear">Limpiar</button>
            <span className="text-[10px] text-slate-400">{signature ? "✓ Firma capturada" : "Pendiente"}</span>
          </div>
        </div>

        <Button onClick={submit} disabled={!signature || busy}
                data-testid="epp-kiosk-confirm"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 rounded-xl">
          {busy ? "Enviando…" : "✓ Confirmar recepción y firma"}
        </Button>
      </div>
    </div>
  );
}
